import { useEffect, useRef, useState, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

export default function PdfViewer({ url, title }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [failed, setFailed] = useState(false);

  // Memoize the file prop — react-pdf reloads the whole PDF if this reference
  // changes between renders, and an inline object literal creates a new one
  // every time. That kicks off an infinite load loop when combined with the
  // ResizeObserver-driven width updates below.
  const file = useMemo(() => ({ url, withCredentials: true }), [url]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Round to integer px — sub-pixel layout jitter would otherwise re-render
    // every <Page> and rasterize the PDF each frame.
    const measure = () => setWidth((prev) => {
      const next = Math.round(el.clientWidth);
      return next === prev ? prev : next;
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (failed) {
    return (
      <div className="border border-[#27272A] rounded-sm bg-[#0A0A0A] p-6 text-center" data-testid="training-lesson-pdf-fallback">
        <p className="text-sm text-[#A1A1AA] mb-3">Couldn't display this PDF inline.</p>
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-[#7EC4EA] underline">Open PDF in new tab</a>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full max-h-[75vh] overflow-y-auto rounded-sm border border-[#27272A] bg-[#0A0A0A]" data-testid="training-lesson-pdf">
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={() => setFailed(true)}
        loading={
          <div className="py-16 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#A1A1AA]" />
          </div>
        }
        externalLinkTarget="_blank"
      >
        {width > 0 && numPages
          ? Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i}
                pageNumber={i + 1}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className={i < numPages - 1 ? "border-b border-[#27272A]" : ""}
                loading={null}
              />
            ))
          : null}
      </Document>
      {numPages > 1 && (
        <div className="sticky bottom-0 text-center text-[10px] font-mono text-white/50 bg-black/60 backdrop-blur-sm py-1" aria-label={`${numPages} pages`}>
          {numPages} pages — scroll to read
        </div>
      )}
    </div>
  );
}
